import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Slider from "rc-slider";
import Select from "react-select";
import { Button, Form, Row, Col } from "react-bootstrap";

import "rc-slider/assets/index.css";
import "./Users.css";
import { BACKEND_BASE } from "./constant";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
const Users = () => {
  const [data, setData] = useState([]);
  const [userCount, setUserCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedName, setSavedName] = useState("");
  const [savedFilters, setSavedFilters] = useState({});
  const [shouldFetch, setShouldFetch] = useState(false);
  const [nameInput, setNameInput] = useState(null);

  // Load saved filters on mount
  useEffect(() => {
    const cached = JSON.parse(localStorage.getItem("savedFilters") || "{}");
    setSavedFilters(cached);
  }, []);
  const saveCurrentFilters = () => {
    const cached = JSON.parse(localStorage.getItem("savedFilters") || "{}");
    cached[savedName] = filters;
    localStorage.setItem("savedFilters", JSON.stringify(cached));
    setSavedFilters(cached);
    setSavedName("");
    setShowSaveModal(false);
  };

  const applySavedFilter = (filterKey) => {
    const filterSet = savedFilters[filterKey];
    if (filterSet) {
      setFilters(filterSet);
      setShouldFetch(true); // Triggers the effect below
    }
  };

  const deleteSavedFilter = (keyToDelete) => {
    const cached = JSON.parse(localStorage.getItem("savedFilters") || "{}");
    delete cached[keyToDelete];
    localStorage.setItem("savedFilters", JSON.stringify(cached));
    setSavedFilters(cached);
  };

  const loadNameOptions = async (inputValue) => {
    if (inputValue.length < 3) return [];

    try {
      const res = await axios.post(`${BACKEND_BASE}/suggestNames`, {
        input: inputValue,
      });

      return res.data.map((name) => ({
        label: name,
        value: name,
      }));
    } catch (err) {
      console.error("Failed to load name suggestions", err);
      return [];
    }
  };
  const handleNameSelect = async (selectedOption) => {
    setNameInput(selectedOption); // Always update the value, even if null

    if (!selectedOption) {
      // Clear results when input is cleared
      setData([]);
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${BACKEND_BASE}/getSpecificUsers`, {
        firstName: selectedOption.value,
      });
      setData(res.data);
    } catch (err) {
      console.error("Name search error", err);
    } finally {
      setLoading(false);
    }
  };

  const [filters, setFilters] = useState({
    ageRange: [18, 60],
    community: "",
    gender: "",
    immigration: [],
    email: "",
  });

  useEffect(() => {
    if (shouldFetch) {
      handleSearch();
      setShouldFetch(false); // reset flag
    }
  }, [filters, shouldFetch]);

  // Fetch total number of users on mount
  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_BASE}/numberOfUsers`);
        setUserCount(res.data.count);
      } catch (err) {
        console.error("Error fetching user count:", err);
      }
    };
    fetchUserCount();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatESTDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      timeZone: "America/New_York",
      hour12: true,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const payload = {};

      if (filters.ageRange.length === 2) {
        payload.minAge = filters.ageRange[0];
        payload.maxAge = filters.ageRange[1];
      }

      if (filters.community) payload.community = filters.community;
      if (filters.gender) payload.gender = filters.gender;
      if (filters.email?.trim())
        payload.email = filters.email.trim().toLowerCase();
      if (filters.previousMaritalStatus)
        payload.previousMaritalStatus = filters.previousMaritalStatus;
      if (filters.immigration.length > 0) {
        payload.immigration = filters.immigration.map((opt) => opt.value);
      }

      const res = await axios.post(`${BACKEND_BASE}/getSpecificUsers`, payload);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching filtered data", error);
    } finally {
      setLoading(false);
    }
  };

  const convertToCSV = (data) => {
    const headers = [
      "Name",
      "ProfileFor",
      "Community",
      "Email",
      "Gender",
      "Intention",
      "Height",
      "Job Title",
      "Personality",
      "Hobbies",
      "Drinks",
      "Smokes",
      "City",
      "State",
      "DobYear",
      "Religion",
      "Diet",
      "Marital Status",
      "Immigration Status",
      "Created At",
    ];

    const escapeCSVValue = (value) => {
      if (typeof value === "string") {
        value = value.replace(/"/g, '""');
        if (value.includes(",") || value.includes("\n")) {
          return `"${value}"`;
        }
      }
      return value;
    };

    const csvRows = [headers.join(",")];

    data.forEach((item) => {
      const row = [
        escapeCSVValue(`${item.firstName || "N/A"} ${item.lastName || "N/A"}`),
        escapeCSVValue(item.profileFor || "N/A"),
        escapeCSVValue(item.community || "N/A"),
        escapeCSVValue(item.email || "N/A"),
        escapeCSVValue(item.gender || "N/A"),
        escapeCSVValue(item.intention || "N/A"),
        escapeCSVValue(item.height || "N/A"),
        escapeCSVValue(item.jobTitle || "N/A"),
        escapeCSVValue(
          Array.isArray(item.personality) ? item.personality.join(", ") : "N/A",
        ),
        escapeCSVValue(
          Array.isArray(item.hobbies) ? item.hobbies.join(", ") : "N/A",
        ),
        escapeCSVValue(item.drinks || "N/A"),
        escapeCSVValue(item.smokes || "N/A"),
        escapeCSVValue(item.city || "N/A"),
        escapeCSVValue(item.state || "N/A"),
        escapeCSVValue(item.dobYear || "N/A"),
        escapeCSVValue(item.religion || "N/A"),
        escapeCSVValue(item.diet || "N/A"),
        escapeCSVValue(item.previousMaritalStatus || "N/A"),
        escapeCSVValue(item.immigration || "N/A"),
        escapeCSVValue(
          item.createdAt !== null ? formatESTDate(item.createdAt) : "N/A",
        ),
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  const downloadCSV = () => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-container">
      <div className="mb-3 d-flex">
        <Button
          className="sticky-back-button"
          onClick={() => navigate("/")}
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
        >
          Back
        </Button>
        <Button
          onClick={downloadCSV}
          className="sticky-back-button ms-3"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
          disabled={data.length === 0}
        >
          Export to CSV
        </Button>
      </div>

      <div className="my-3">
        <h5>
          Total Users in Database:{" "}
          {userCount !== null ? userCount : "Loading..."}
        </h5>
      </div>

      <Form className="mb-4">
        <Row>
          <Col md={3}>
            <Form.Group controlId="nameSearch">
              <Form.Label>First Name</Form.Label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadNameOptions}
                defaultOptions={false}
                onChange={handleNameSelect}
                value={nameInput}
                placeholder="Type at least 3 letters..."
                isClearable
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="emailSearch">
              <Form.Label>Email</Form.Label>

              <Form.Control
                type="email"
                name="email"
                placeholder="Enter email"
                value={filters.email}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="community">
              <Form.Label>Community</Form.Label>
              <Form.Control
                as="select"
                name="community"
                value={filters.community}
                onChange={handleFilterChange}
              >
                {[
                  "Select",
                  "Gujarati",
                  "Bengali",
                  "Guyanese",
                  "Hindi Speaking",
                  "Jatt",
                  "Kannada",
                  "Kashmiri",
                  "Malayali",
                  "Marathi",
                  "Marwari",
                  "Odia",
                  "Punjabi",
                  "Rajasthani",
                  "Shia",
                  "Sindhi",
                  "Sunni",
                  "Tamil",
                  "Telugu",
                  "Urdu",
                ].map((c) => (
                  <option key={c} value={c === "Select" ? "" : c}>
                    {c}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="previousMaritalStatus">
              <Form.Label>Marital Status</Form.Label>
              <Form.Control
                as="select"
                name="previousMaritalStatus"
                value={filters.previousMaritalStatus}
                onChange={handleFilterChange}
              >
                {[
                  "Select",
                  "Single, never married",
                  "Divorced, no kids",
                  "Divorced, with kids",
                  "Widowed, no kids",
                  "Widowed, with kids",
                ].map((c) => (
                  <option key={c} value={c === "Select" ? "" : c}>
                    {c}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <div className="p-1">
              <Form.Group controlId="ageRange">
                <Form.Label>
                  Age Range: {filters.ageRange[0]} – {filters.ageRange[1]}
                </Form.Label>
                <Slider
                  range
                  min={18}
                  max={80}
                  value={filters.ageRange}
                  onChange={(range) =>
                    setFilters((prev) => ({ ...prev, ageRange: range }))
                  }
                  trackStyle={[{ backgroundColor: "#48276E" }]}
                  handleStyle={[
                    { borderColor: "#48276E" },
                    { borderColor: "#48276E" },
                  ]}
                />
              </Form.Group>
            </div>
          </Col>
          <Col md={3}>
            <Form.Group controlId="gender">
              <Form.Label>Gender</Form.Label>
              <Form.Control
                as="select"
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="immigration">
              <Form.Label>Immigration Status</Form.Label>
              <Select
                isMulti
                name="immigration"
                options={[
                  { value: "US Citizen", label: "US Citizen" },
                  { value: "Greencard Holder", label: "Greencard Holder" },
                  { value: "Work Visa", label: "Work Visa" },
                  { value: "Student Visa", label: "Student Visa" },
                  { value: "Other", label: "Other" },
                ]}
                value={filters.immigration}
                onChange={(selected) =>
                  setFilters((prev) => ({ ...prev, immigration: selected }))
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <Button
          onClick={handleSearch}
          className="mt-3"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
        >
          Search
        </Button>
        <Button
          onClick={() => setData([])}
          className="mt-3 ms-2"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
        >
          Clear
        </Button>
        <Button
          onClick={() => setShowSaveModal(true)}
          className="mt-3 ms-2 rounded-pill"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
        >
          +
        </Button>
      </Form>
      <p>
        <div className="mt-4">
          <h5>Saved Filters:</h5>
          {Object.keys(savedFilters).length === 0 ? (
            <p>No saved filters</p>
          ) : (
            <ul className="list-unstyled">
              {Object.entries(savedFilters).map(([key, value]) => (
                <li key={key} className="d-flex align-items-center mb-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="me-2"
                    onClick={() => applySavedFilter(key)}
                  >
                    {key}
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="rounded-pill"
                    size="sm"
                    onClick={() => deleteSavedFilter(key)}
                  >
                    &minus;
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </p>
      {showSaveModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-content-custom">
            <h5>Save Current Filters</h5>
            <input
              className="form-control mt-2"
              type="text"
              placeholder="Enter filter name"
              value={savedName}
              onChange={(e) => setSavedName(e.target.value)}
            />
            <div className="mt-3 d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={() => setShowSaveModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button
                style={{ backgroundColor: "rgb(72, 39, 110)" }}
                onClick={saveCurrentFilters}
                disabled={!savedName.trim()}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center my-2">Loading...</p>
      ) : data.length > 0 ? (
        <div className="table-container">
          <table className="styled-table">
            <thead>
              <tr>
                {[
                  "#",
                  "Name",
                  "ProfileFor",
                  "Community",
                  "Email",
                  "Gender",
                  "Intention",
                  "Height",
                  "Job Title",
                  "Personality",
                  "Hobbies",
                  "Drinks",
                  "Smokes",
                  "City",
                  "State",
                  "DobYear",
                  "Religion",
                  "Diet",
                  "Marital Status",
                  "Immigration Status",
                  "Images",
                  "Created At",
                ].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data
                .slice()
                .reverse()
                .map((item, index) => (
                  <tr key={item._id}>
                    <td>{index + 1}</td>
                    <td>{`${item.firstName || ""} ${item.lastName || ""}`}</td>
                    <td>{item.profileFor || "N/A"}</td>
                    <td>{item.community || "N/A"}</td>
                    <td>{item.email || "N/A"}</td>
                    <td>{item.gender || "N/A"}</td>
                    <td>{item.intention || "N/A"}</td>
                    <td>{item.height || "N/A"}</td>
                    <td>{item.jobTitle || "N/A"}</td>
                    <td>
                      {Array.isArray(item.personality)
                        ? item.personality.join(", ")
                        : "N/A"}
                    </td>
                    <td>
                      {Array.isArray(item.hobbies)
                        ? item.hobbies.join(", ")
                        : "N/A"}
                    </td>
                    <td>{item.drinks || "N/A"}</td>
                    <td>{item.smokes || "N/A"}</td>
                    <td>{item.city || "N/A"}</td>
                    <td>{item.state || "N/A"}</td>
                    <td>{item.dobYear || "N/A"}</td>
                    <td>{item.religion || "N/A"}</td>
                    <td>{item.diet || "N/A"}</td>
                    <td>{item.previousMaritalStatus || "N/A"}</td>
                    <td>{item.immigration || "N/A"}</td>
                    <td>
                      <div className="user-images-container">
                        {item.userImages?.length > 0 ? (
                          item.userImages.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`User ${item.firstName} pic ${idx + 1}`}
                              className="user-image"
                              onClick={() => window.open(url, "_blank")}
                            />
                          ))
                        ) : (
                          <span>No Images</span>
                        )}
                      </div>
                    </td>
                    <td>{formatESTDate(item.createdAt) || "N/A"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center mt-3">
          No users found. Try adjusting the filters.
        </p>
      )}
    </div>
  );
};

export default Users;
